import { useCallback, useEffect, useRef, useState } from "react";
import { PLACE_NAMES, type Place } from "./place";
import { VisitorMap } from "./VisitorMap";
import { admitVisitor, boudoirMembers, nameVisitor, visitLog, visitorProfiles, visitStats, type VisitDoing, type VisitLogEntry, type VisitorProfile, type VisitStats } from "./visits";
import { messageOf } from "./useCollection";

/**
 * The catalogue's visitor book: how many people have come to the Palais, how
 * many of them are different people, and where they came from. Only editors
 * see it; the database refuses everyone else.
 */

export const VISIT_RANGES = [
  { days: 1, label: "Today" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 3650, label: "All time" },
];

const RANGES = VISIT_RANGES;
const ALL_TIME = 3650;

const flag = (code: string | null) =>
  code && /^[A-Z]{2}$/i.test(code)
    ? String.fromCodePoint(...code.toUpperCase().split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
    : "🌐";

const DEVICE_ICON: Record<string, string> = { phone: "📱", tablet: "📲", desktop: "💻" };
const deviceIcon = (d: string | null) => DEVICE_ICON[d ?? ""] ?? "❔";
const deviceName = (d: string | null) => (d ? d.charAt(0).toUpperCase() + d.slice(1) : "Unknown");

const ago = (iso: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/** the pages of the portfolio, which are stops on the same walk as the rooms
    (MainComponent.tsx). They are written down as "page-projects"; someone who
    came in on one has it as a path instead. */
const PAGE_NAMES: Record<string, string> = {
  home: "the Palais",
  overview: "Overview",
  portfolio: "Overview",
  projects: "Projects",
  experience: "Experience",
  education: "Education",
  skills: "Skills",
  awards: "Awards",
  certifications: "Certifications",
  resume: "Resume",
  admin: "the visitor book",
  "closet/dress": "the dress form",
  dressform: "the dress form",
};

const placeName = (p: string) =>
  PLACE_NAMES[p as Place] ?? PAGE_NAMES[p.replace(/^(page-|\/)/, "")] ?? p;

/** what someone did inside, read back as a sentence: "opened the catalogue ·
    characters: Molly → Ella". People they scrolled past in a row are gathered
    up rather than listed one line each. */
function doingTrail(doings: VisitDoing[]) {
  const bits: string[] = [];
  for (const d of doings) {
    if (d.kind === "character") {
      const last = bits[bits.length - 1];
      if (last?.startsWith("characters: ")) bits[bits.length - 1] = `${last} → ${d.detail}`;
      else bits.push(`characters: ${d.detail}`);
    } else if (d.kind === "characters") {
      bits.push("opened the character catalogue");
    } else if (d.kind === "catalogue") {
      bits.push("opened the catalogue");
    } else if (d.kind === "record") {
      bits.push("put the record on");
    } else if (d.kind === "pictures") {
      bits.push("looked through the pictures");
    } else if (d.kind === "portrait-seen" || d.kind === "portrait-film") {
      // the ones they stopped on, gathered into one trail like the characters
      const word = d.kind === "portrait-film" ? "watched: " : "pictures: ";
      const last = bits[bits.length - 1];
      if (last?.startsWith(word)) bits[bits.length - 1] = `${last} → ${d.detail}`;
      else bits.push(`${word}${d.detail}`);
    } else if (d.kind === "dressform") {
      bits.push(d.detail ? `stood at the dress form · ${d.detail}` : "stood at the dress form");
    } else if (d.kind === "portrait-note") {
      bits.push(d.detail ? `left a note on ${d.detail}` : "left a note on a portrait");
    } else if (d.kind === "portrait-try") {
      bits.push(d.detail ? `tried a word on the dresser · ${d.detail}` : "tried a word on the dresser");
    } else if (d.kind === "portrait") {
      bits.push(d.detail ? `put ${d.detail} in the dresser` : "put something in the dresser");
    } else if (d.kind === "letters") {
      bits.push(d.detail ? `opened the letters · ${d.detail}` : "opened the letters");
    } else {
      bits.push(d.detail ? `${d.kind}: ${d.detail}` : d.kind);
    }
  }
  return bits.join(" · ");
}

/** a stop on the walk, with how long they stood in it: "Awards (22s)". The
    one they're still standing in has no seconds yet, and just gives its name. */
const stopName = (stop: string | { place: string; seconds: number | null }) => {
  if (typeof stop === "string") return placeName(stop);
  const held = stop.seconds ?? 0;
  return held > 0 ? `${placeName(stop.place)} (${mins(held)})` : placeName(stop.place);
};

const mins = (sec: number | null | undefined) => {
  const n = sec ?? 0;
  if (!n) return "—";
  if (n < 60) return `${Math.round(n)}s`;
  if (n < 3600) return `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`;
  return `${Math.floor(n / 3600)}h ${Math.round((n % 3600) / 60)}m`;
};

const hour = (h: number | null) => (h === null ? "" : `${((h + 11) % 12) + 1}${h < 12 ? "am" : "pm"}`);

const machineOf = (v: VisitLogEntry) =>
  [
    v.brand && v.model ? `${v.brand} ${v.model}` : v.brand ?? v.model,
    [v.os, v.os_version].filter(Boolean).join(" "),
    [v.browser, v.browser_version?.split(".")[0]].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(" · ");

const dateOf = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/** how often a visitor comes, in words */
function frequency(p: VisitorProfile) {
  if (p.visits <= 1) return "Came once";
  const spanDays = Math.max(1, (new Date(p.last_at).getTime() - new Date(p.first_at).getTime()) / 86_400_000);
  const perWeek = (p.visits / spanDays) * 7;
  if (p.days_active / (spanDays + 1) >= 0.6) return "Almost every day";
  if (perWeek >= 3) return "Several times a week";
  if (perWeek >= 1) return "About weekly";
  if (perWeek >= 0.25) return "A few times a month";
  return "Now and then";
}

const byVisits = <T extends { visits: number }>(xs: T[]) => [...xs].sort((a, b) => b.visits - a.visits);

const who = (name: string | null, visitor: string | null, isMe = false) => (isMe ? `${name ?? "Molly"} (me)` : name ?? (visitor ? `visitor ${visitor}` : "someone"));

/** Two visitors you've given the same name are the same person (their code
    changes as their address does), so fold their profiles into one card that
    lists every id they've come in under and adds up what they did. Unnamed
    visitors are left on their own. */
export type MergedProfile = VisitorProfile & { ids: string[] };

const foldPeople = (g: VisitorProfile[]): MergedProfile => {
  const lead = g.reduce((a, b) => (b.visits > a.visits ? b : a));
  const uniq = (xs: (string | null)[]) => Array.from(new Set(xs.filter(Boolean) as string[]));
  const tally = <T,>(items: T[], id: (t: T) => string, n: (t: T) => number, make: (t: T, v: number) => T): T[] => {
    const m = new Map<string, { t: T; v: number }>();
    for (const it of items) {
      const k = id(it);
      const cur = m.get(k);
      if (cur) cur.v += n(it);
      else m.set(k, { t: it, v: n(it) });
    }
    return Array.from(m.values())
      .sort((a, b) => b.v - a.v)
      .map(({ t, v }) => make(t, v));
  };
  const mapPlaces = (() => {
    const m = new Map<string, { place: string; visits: number; seconds: number }>();
    for (const x of g.flatMap((p) => p.map_places)) {
      const cur = m.get(x.place);
      if (cur) { cur.visits += x.visits; cur.seconds += x.seconds; }
      else m.set(x.place, { ...x });
    }
    return Array.from(m.values()).sort((a, b) => b.visits - a.visits);
  })();
  return {
    ...lead,
    ids: g.map((p) => p.visitor),
    is_me: g.some((p) => p.is_me),
    visits: g.reduce((s, p) => s + p.visits, 0),
    days_active: g.reduce((s, p) => s + p.days_active, 0),
    first_at: g.reduce((m, p) => (p.first_at < m ? p.first_at : m), g[0].first_at),
    last_at: g.reduce((m, p) => (p.last_at > m ? p.last_at : m), g[0].last_at),
    last_7: g.reduce((s, p) => s + p.last_7, 0),
    last_30: g.reduce((s, p) => s + p.last_30, 0),
    note: g.map((p) => p.note).find(Boolean) ?? null,
    total_seconds: g.reduce((s, p) => s + p.total_seconds, 0),
    longest_seconds: g.reduce((m, p) => Math.max(m, p.longest_seconds), 0),
    busiest_hour: lead.busiest_hour,
    timezone: uniq(g.map((p) => p.timezone)).join(", ") || null,
    language: uniq(g.map((p) => p.language)).join(", ") || null,
    network: uniq(g.map((p) => p.network)).join(", ") || null,
    vpn_visits: g.reduce((s, p) => s + p.vpn_visits, 0),
    sources: tally(g.flatMap((p) => p.sources), (s) => s.name, (s) => s.visits, (s, v) => ({ ...s, visits: v })),
    places: tally(g.flatMap((p) => p.places), (x) => `${x.name}|${x.code ?? ""}`, (x) => x.visits, (x, v) => ({ ...x, visits: v })),
    map_places: mapPlaces,
    devices: tally(
      g.flatMap((p) => p.devices),
      (d) => `${d.device}|${d.brand}|${d.model}|${d.os}|${d.browser}|${d.screen}`,
      (d) => d.visits,
      (d, v) => ({ ...d, visits: v }),
    ),
  };
};

export const mergeByName = (people: VisitorProfile[]): MergedProfile[] => {
  const groups = new Map<string, VisitorProfile[]>();
  for (const p of people) {
    const named = p.name?.trim();
    const key = named ? `name:${named.toLowerCase()}` : `id:${p.visitor}`;
    const g = groups.get(key);
    if (g) g.push(p);
    else groups.set(key, [p]);
  }
  return Array.from(groups.values())
    .map((g) => (g.length === 1 ? { ...g[0], ids: [g[0].visitor] } : foldPeople(g)))
    .sort((a, b) => b.visits - a.visits);
};

const where = (v: { city: string | null; region: string | null; country: string | null }) => {
  const parts = [v.city, v.region, v.country].filter(Boolean) as string[];
  return parts.filter((x, i) => parts.indexOf(x) === i).join(", ") || "Somewhere";
};

/** `only` fixes the stretch of time from outside (the /admin page has its own
    picker), which also takes this one's away */
export function VisitorsShelf({ forDays }: { forDays?: number } = {}) {
  const [own, setDays] = useState(30);
  const days = forDays ?? own;
  const [stats, setStats] = useState<VisitStats | null>(null);
  const [error, setError] = useState("");

  // every visitor, with how often they come (all time)
  const [people, setPeople] = useState<VisitorProfile[] | null>(null);
  const [peopleError, setPeopleError] = useState("");
  const loadPeople = useCallback(() => {
    visitorProfiles()
      .then((p) => {
        setPeople(p);
        setPeopleError("");
      })
      .catch((e) => setPeopleError(messageOf(e)));
  }, []);
  useEffect(loadPeople, [loadPeople]);

  /* who's been let into the Boudoir (by visitor code / IP). An "Admit" switch
     on each visitor flips it, so the dresser is opened to a person, not an
     account. */
  const [admitted, setAdmitted] = useState<Set<string>>(new Set());
  const [admitting, setAdmitting] = useState<string | null>(null);
  useEffect(() => {
    boudoirMembers()
      .then((ms) => setAdmitted(new Set(ms.filter((m) => m.admitted).map((m) => m.visitor))))
      .catch(() => {
        /* the migration may not be run yet — the switch just starts "Admit" */
      });
  }, []);
  const toggleAdmit = useCallback(async (ids: string[], on: boolean) => {
    setAdmitting(ids[0]);
    try {
      for (const id of ids) await admitVisitor(id, on);
      setAdmitted((was) => {
        const next = new Set(was);
        ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
        return next;
      });
    } catch (e) {
      setPeopleError(messageOf(e));
    } finally {
      setAdmitting(null);
    }
  }, []);

  // the visit log: every visit, a page at a time, as far back as you scroll
  const [only, setOnly] = useState<string | null>(null);
  /* Molly is in the feed every day, which buries everybody else, so her own
     visits are kept out of it. The button by the heading brings them back. */
  const [hideMe, setHideMe] = useState(true);
  const [log, setLog] = useState<VisitLogEntry[]>([]);
  const [logDone, setLogDone] = useState(false);
  const [logError, setLogError] = useState("");
  const [loadingAll, setLoadingAll] = useState(false);
  const loading = useRef(false);
  const more = useCallback(
    (reset = false) => {
      if (loading.current) return;
      loading.current = true;
      const before = reset ? undefined : log[log.length - 1]?.id;
      visitLog(before, only)
        .then((page) => {
          setLog((l) => (reset ? page : [...l, ...page]));
          setLogDone(page.length < 50);
          setLogError("");
        })
        .catch((e) => setLogError(messageOf(e)))
        .finally(() => {
          loading.current = false;
        });
    },
    [log, only],
  );
  // start again when the filter changes
  const lastOnly = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastOnly.current === only) return;
    lastOnly.current = only;
    setLog([]);
    setLogDone(false);
    loading.current = false;
    more(true);
  }, [only, more]);
  // load the next page when the end of the log scrolls into view
  const end = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const el = end.current;
    if (!el || logDone) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && more(), { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [more, logDone, log.length]);

  // "Load all": page all the way to the end in one go, rather than scrolling
  const loadAll = useCallback(async () => {
    if (loading.current || loadingAll) return;
    setLoadingAll(true);
    loading.current = true; // hold off the scroll loader while this runs
    try {
      let before = log[log.length - 1]?.id;
      for (let guard = 0; guard < 500; guard++) {
        const page = await visitLog(before, only);
        if (page.length) setLog((l) => [...l, ...page]);
        before = page[page.length - 1]?.id;
        if (page.length < 50) break; // a short page is the last page
      }
      setLogDone(true);
      setLogError("");
    } catch (e) {
      setLogError(messageOf(e));
    } finally {
      loading.current = false;
      setLoadingAll(false);
    }
  }, [log, only, loadingAll]);

  // the admin page is meant to show the whole picture, so once the first page
  // is in, pull the rest on its own — Countries, Cities and the feed come out
  // complete with no tapping. (The buttons stay as a manual nudge just in case.)
  useEffect(() => {
    if (log.length && !logDone && !loadingAll && !loading.current) void loadAll();
  }, [log.length, logDone, loadingAll, loadAll]);

  const rename = async (p: VisitorProfile) => {
    // anyone named Molly is counted as Molly, however it is typed: her address
    // changes often enough that she arrives as a new code every few days
    const name = window.prompt(
      `Name for visitor ${p.visitor} — call them Molly to hide their visits behind “Show my own visits”, or leave empty to remove the name`,
      p.name ?? "",
    );
    if (name === null) return;
    try {
      await nameVisitor(p.visitor, name, p.is_me);
      loadPeople();
      setLog([]);
      lastOnly.current = undefined;
      setOnly((o) => o);
      more(true);
    } catch (e) {
      setPeopleError(messageOf(e));
    }
  };

  const needsMigration = (e: string) => /does not exist|Could not find/i.test(e);

  useEffect(() => {
    let live = true;
    setError("");
    visitStats(days)
      .then((s) => live && setStats(s))
      .catch((e) => live && setError(messageOf(e)));
    return () => {
      live = false;
    };
  }, [days]);

  /* The map pins every city anyone has ever come from, the same as the
     Cities list beside it, whatever stretch of time the picker is on. */
  const [everywhere, setEverywhere] = useState<VisitStats | null>(null);
  useEffect(() => {
    if (days === ALL_TIME) return;
    let live = true;
    visitStats(ALL_TIME)
      .then((s) => live && setEverywhere(s))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [days]);
  const mapStats = days === ALL_TIME ? stats : everywhere ?? stats;

  /* everybody else's visits, unless the button says otherwise */
  const feed = hideMe && !only ? log.filter((v) => !v.is_me) : log;
  // visitors you've named the same are folded into one card (see mergeByName)
  const folk = people ? mergeByName(people) : null;

  /* Countries and cities are counted in visits, not visitors: someone who
     comes back ten times counts ten times. The server only sends the busiest
     handful of countries and cities (ranked by visitors, so they're re-sorted
     here). Once
     "Load all" has pulled every visit into the log, fold the FULL lists from
     it instead, so nowhere a visitor came from is left off. */
  const logCountries =
    logDone && log.length
      ? (() => {
          const m = new Map<string, { country: string; code: string | null; visits: number }>();
          for (const v of log) {
            const key = v.country ?? "Unknown";
            const cur = m.get(key) ?? { country: key, code: v.code, visits: 0 };
            cur.visits += 1;
            if (!cur.code && v.code) cur.code = v.code;
            m.set(key, cur);
          }
          return Array.from(m.values()).sort((a, b) => b.visits - a.visits);
        })()
      : null;
  const logCities =
    logDone && log.length
      ? (() => {
          const m = new Map<string, { city: string; region: string | null; country: string | null; code: string | null; visits: number }>();
          for (const v of log) {
            if (!v.city) continue;
            const key = `${v.city}|${v.code ?? ""}`;
            const cur = m.get(key) ?? { city: v.city, region: v.region, country: v.country, code: v.code, visits: 0 };
            cur.visits += 1;
            m.set(key, cur);
          }
          return Array.from(m.values()).sort((a, b) => b.visits - a.visits);
        })()
      : null;
  const max = Math.max(1, ...(stats?.by_day.map((d) => d.visits) ?? [1]));
  const period = RANGES.find((r) => r.days === days)?.label ?? "";

  return (
    <div className="cat-looks vis">
      {!forDays && (
      <nav className="cat-seasons" aria-label="Time range">
        {RANGES.map((r) => (
          <button
            key={r.days}
            type="button"
            className={`cat-season${days === r.days ? " is-on" : ""}`}
            aria-pressed={days === r.days}
            onClick={() => setDays(r.days)}
          >
            {r.label}
          </button>
        ))}
      </nav>
      )}

      {error && (
        <p className="cat-error" role="alert">
          {/does not exist|Could not find/i.test(error)
            ? "Visitor counting isn't set up yet: run the palais_visits SQL in Supabase."
            : error}
        </p>
      )}

      {!stats && !error && <p className="cat-note">Counting visitors…</p>}

      {stats && (
        <>
          <div className="vis-tiles">
            <div className="vis-tile">
              <span className="vis-num">{stats.period.unique.toLocaleString()}</span>
              <span className="vis-label">unique visitors · {period}</span>
            </div>
            <div className="vis-tile">
              <span className="vis-num">{stats.period.visits.toLocaleString()}</span>
              <span className="vis-label">visits · {period}</span>
            </div>
            <div className="vis-tile vis-tile--soft">
              <span className="vis-num">{stats.all_time.unique.toLocaleString()}</span>
              <span className="vis-label">unique, all time ({stats.all_time.visits.toLocaleString()} visits)</span>
            </div>
          </div>

          {stats.by_day.length > 1 && (
            <section className="vis-card">
              <h3>♡ Visits per day</h3>
              <div className="vis-bars" role="img" aria-label={`Visits per day over ${period}`}>
                {stats.by_day.map((d) => (
                  <span
                    key={d.day}
                    className="vis-bar"
                    style={{ height: `${Math.max(4, (d.visits / max) * 100)}%` }}
                    title={`${d.day}: ${d.visits} visits, ${d.unique} unique`}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="vis-card">
            <h3>🔗 Where they came from</h3>
            {stats.sources?.length || stats.me?.visits ? (
              <ul className="vis-list vis-list--sources">
                {!!stats.me?.visits && (
                  <li className="vis-me">
                    <span>
                      Me
                      <small>
                        {stats.me.visits} visit{stats.me.visits === 1 ? "" : "s"} · {Math.round((stats.me.visits / Math.max(1, stats.period.visits)) * 100)}%
                      </small>
                    </span>
                    <b>1</b>
                  </li>
                )}
                {(stats.sources ?? []).map((s) => (
                  <li key={s.source}>
                    <span>
                      {s.source}
                      <small>
                        {s.visits} visit{s.visits === 1 ? "" : "s"} · {Math.round((s.visits / Math.max(1, stats.period.visits)) * 100)}%
                      </small>
                    </span>
                    <b>{s.unique}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="cat-note">No visits with a source yet.</p>
            )}
            <p className="vis-hint">
              iMessage, WhatsApp and the Discord app don't pass this along. To see them, share a tagged link like{" "}
              <code>mollybeach.app/?from=discord</code>.
            </p>
          </section>

          <section className="vis-card">
            <h4>🚪 Came in through</h4>
            {stats.landings?.length ? (
              <ul className="vis-list">
                {stats.landings.map((l) => (
                  <li key={l.landing}>
                    <span>
                      {l.room ?? l.landing}
                      <small>
                        {l.landing === "/" ? "the front door" : `mollybeach.app/${l.landing}`} · {l.visits} visit
                        {l.visits === 1 ? "" : "s"}
                      </small>
                    </span>
                    <b>{l.unique}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="cat-note">No arrivals recorded yet{stats.landings ? "" : " (run the front doors SQL to start)"}.</p>
            )}
            <p className="vis-hint">
              A link straight to a room — <code>mollybeach.app/lakehouse</code> — lands someone there rather than in the
              palace, and shows up here.
            </p>
          </section>

          <section className="vis-card">
            <h3>🗺️ Where they went on the map</h3>
            {stats.places?.length ? (
              <ul className="vis-list">
                {stats.places.map((p) => (
                  <li key={p.place}>
                    <span>
                      {placeName(p.place)}
                      <small>
                        {p.visits} time{p.visits === 1 ? "" : "s"}
                        {p.seconds ? ` · about ${mins(p.seconds)} each` : ""}
                      </small>
                    </span>
                    <b>{p.unique}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="cat-note">No map walks recorded yet{stats.places ? "" : " (run the visitor names SQL to start)"}.</p>
            )}
          </section>

          {!!stats.time && (
            <section className="vis-card">
              <h3>⏱ How long they stay</h3>
              <ul className="vis-list">
                <li>
                  <span>Typical visit</span>
                  <b>{mins(stats.time.median_seconds)}</b>
                </li>
                <li>
                  <span>Longest visit</span>
                  <b>{mins(stats.time.longest_seconds)}</b>
                </li>
                <li>
                  <span>
                    Quick glances<small>under 10 seconds</small>
                  </span>
                  <b>{stats.time.glances}</b>
                </li>
              </ul>
            </section>
          )}

          <div className="vis-cols">
            <section className="vis-card">
              <h3>📱 Devices</h3>
              {stats.devices?.length ? (
                <ul className="vis-list">
                  {stats.devices.map((d) => (
                    <li key={d.device}>
                      <span>
                        {deviceIcon(d.device)} {deviceName(d.device)}
                        <small>
                          {d.visits} visit{d.visits === 1 ? "" : "s"} ·{" "}
                          {Math.round((d.visits / Math.max(1, stats.period.visits)) * 100)}%
                        </small>
                      </span>
                      <b>{d.unique}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cat-note">No visits yet.</p>
              )}
            </section>
            <section className="vis-card">
              <h3>🧭 System &amp; browser</h3>
              {stats.systems?.length ? (
                <ul className="vis-list">
                  {stats.systems.map((s) => (
                    <li key={`${s.os}-${s.browser}`}>
                      <span>
                        {s.os} · {s.browser}
                      </span>
                      <b>{s.unique}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cat-note">No visits yet.</p>
              )}
            </section>
          </div>

          <div className="vis-cols">
            <section className="vis-card">
              <h3>🏷️ Makes &amp; models</h3>
              {stats.makes?.length ? (
                <ul className="vis-list">
                  {stats.makes.map((m) => (
                    <li key={`${m.brand}-${m.model}`}>
                      <span>
                        {m.brand}
                        {m.model ? <small>{m.model}</small> : null}
                      </span>
                      <b>{m.unique}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cat-note">No makes yet: phones only tell us from the next visit on.</p>
              )}
            </section>
            <section className="vis-card">
              <h3>🖥 Screens</h3>
              {stats.screens?.length ? (
                <ul className="vis-list">
                  {stats.screens.map((sc) => (
                    <li key={sc.size}>
                      <span>{sc.size}</span>
                      <b>{sc.unique}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cat-note">No screens yet.</p>
              )}
            </section>
          </div>

          <div className="vis-cols">
            <section className="vis-card">
              <h3>🛡 Likely VPNs</h3>
              {stats.vpn?.visits ? (
                <ul className="vis-list">
                  <li>
                    <span>
                      Visits that look like a VPN
                      <small>of {stats.period.visits} in this stretch</small>
                    </span>
                    <b>{stats.vpn.visits}</b>
                  </li>
                  {stats.vpn.networks.map((n, i) => (
                    <li key={i}>
                      <span>
                        {n.network ?? "Unknown network"}
                        <small>{n.why}</small>
                      </span>
                      <b>{n.visits}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cat-note">Nothing looks like a VPN{stats.vpn ? "" : " yet (run the visitor SQL)"}.</p>
              )}
              <p className="vis-hint">A guess, not proof: offices, travellers and Apple's Private Relay set it off too.</p>
            </section>
            <section className="vis-card">
              <h3>🗣 Languages</h3>
              {stats.languages?.length ? (
                <ul className="vis-list">
                  {stats.languages.map((l) => (
                    <li key={l.language}>
                      <span>{l.language}</span>
                      <b>{l.unique}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cat-note">No languages yet.</p>
              )}
            </section>
            <section className="vis-card">
              <h3>📶 Connection &amp; taste</h3>
              <ul className="vis-list">
                {(stats.connections ?? []).map((c) => (
                  <li key={c.connection}>
                    <span>
                      {c.connection}
                      {c.downlink ? <small>about {c.downlink} Mbps</small> : null}
                    </span>
                    <b>{c.visits}</b>
                  </li>
                ))}
                {!!stats.tastes && (
                  <>
                    <li>
                      <span>Dark mode</span>
                      <b>{stats.tastes.dark}</b>
                    </li>
                    <li>
                      <span>Light mode</span>
                      <b>{stats.tastes.light}</b>
                    </li>
                    <li>
                      <span>Less motion</span>
                      <b>{stats.tastes.reduced_motion}</b>
                    </li>
                    <li>
                      <span>Added to a home screen</span>
                      <b>{stats.tastes.installed}</b>
                    </li>
                  </>
                )}
              </ul>
            </section>
          </div>

          <div className="vis-cols">
            <section className="vis-card">
              <h3>🌍 Countries{logCountries ? ` · ${logCountries.length}` : ""}</h3>
              {(logCountries ?? stats.countries).length ? (
                <ul className="vis-list">
                  {(logCountries ?? byVisits(stats.countries)).map((c) => (
                    <li key={c.country}>
                      <span>
                        {flag(c.code)} {c.country}
                      </span>
                      <b>{c.visits}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cat-note">No visits yet.</p>
              )}
              {!logCountries && (
                <p className="vis-hint vis-more--load">
                  <span>Top {stats.countries.length}.</span>
                  <button type="button" className="cat-mini" onClick={loadAll} disabled={loadingAll}>
                    {loadingAll ? "Loading…" : "Show every country"}
                  </button>
                </p>
              )}
            </section>
            <section className="vis-card">
              <h3>📍 Cities{logCities ? ` · ${logCities.length}` : ""}</h3>
              {(logCities ?? stats.cities).length ? (
                <ul className="vis-list">
                  {(logCities ?? byVisits(stats.cities)).map((c) => (
                    <li key={`${c.city}-${c.code}`}>
                      <span>
                        {flag(c.code)} {where(c)}
                      </span>
                      <b>{c.visits}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cat-note">No cities yet.</p>
              )}
              {!logCities && (stats.cities_total ?? 0) > stats.cities.length && (
                <p className="vis-hint vis-more--load">
                  <span>Top {stats.cities.length} of {stats.cities_total}.</span>
                  <button type="button" className="cat-mini" onClick={loadAll} disabled={loadingAll}>
                    {loadingAll ? "Loading…" : "Show every city"}
                  </button>
                </p>
              )}
            </section>
          </div>

          {/* the two long lists start folded: the page opens as a summary
              rather than a wall of visits */}
          <details className="vis-card vis-fold">
            <summary>
              <h3>👥 Visitors{folk?.length ? ` · ${folk.length}` : ""}</h3>
            </summary>
            {peopleError && (
              <p className="cat-error" role="alert">
                {needsMigration(peopleError) ? "Visitor profiles aren't set up yet: run supabase/migrations/20260915120000_palais_visitor_id.sql." : peopleError}
              </p>
            )}
            {folk && !folk.length && <p className="cat-note">No visitors yet.</p>}
            {folk && folk.length > 0 && (
              <ul className="vis-people">
                {folk.map((p) => (
                  <li key={p.visitor} className={`vis-person${p.is_me ? " is-me" : ""}${only === p.visitor ? " is-on" : ""}`}>
                    <div className="vis-person-head">
                      <strong>{who(p.name, p.visitor, p.is_me)}</strong>
                      <span className="vis-freq">{frequency(p)}</span>
                    </div>
                    {p.ids.length > 1 && (
                      <p className="vis-ids">🔖 {p.ids.length} ids · {p.ids.join(" · ")}</p>
                    )}
                    <p>
                      <b>{p.visits}</b> visit{p.visits === 1 ? "" : "s"} on {p.days_active} day{p.days_active === 1 ? "" : "s"} · {p.last_30} in the last 30 days
                    </p>
                    <p>
                      First {dateOf(p.first_at)} · last {ago(p.last_at)}
                    </p>
                    <p>
                      ⏱ {mins(p.total_seconds)} in all · longest {mins(p.longest_seconds)}
                      {p.busiest_hour !== null ? ` · usually around ${hour(p.busiest_hour)}` : ""}
                    </p>
                    {(p.timezone || p.language || p.network) && (
                      <p>🕰 {[p.timezone, p.language, p.network].filter(Boolean).join(" · ")}</p>
                    )}
                    {p.vpn_visits > 0 && (
                      <p>
                        <b className="vis-vpn">🛡 {p.vpn_visits === p.visits ? "Always" : `${p.vpn_visits} of ${p.visits} visits`}</b> looks like a VPN
                      </p>
                    )}
                    {p.places.length > 0 && <p>📍 {p.places.map((x) => `${flag(x.code)} ${x.name}`).join(" · ")}</p>}
                    {p.map_places.length > 0 && (
                      <p>🗺️ {p.map_places.map((x) => `${placeName(x.place)} ×${x.visits}${x.seconds ? ` (${mins(x.seconds)})` : ""}`).join(" · ")}</p>
                    )}
                    {p.sources.length > 0 && <p>🔗 {p.sources.map((x) => x.name).join(" · ")}</p>}
                    {p.devices.map((d, i) => (
                      <p key={i}>
                        {deviceIcon(d.device)}{" "}
                        {[
                          d.brand && d.model ? `${d.brand} ${d.model}` : d.brand ?? d.model,
                          [d.os, d.os_version].filter(Boolean).join(" "),
                          [d.browser, d.browser_version?.split(".")[0]].filter(Boolean).join(" "),
                          d.screen,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    ))}
                    <div className="vis-person-actions">
                      <button type="button" className="cat-mini" onClick={() => setOnly(only === p.visitor ? null : p.visitor)}>
                        {only === p.visitor ? "Show everyone" : "Their visits"}
                      </button>
                      <button type="button" className="cat-mini" onClick={() => rename(p)}>
                        {p.name ? "Rename" : "Name"}
                      </button>
                      {(() => {
                        const isIn = p.ids.some((id) => admitted.has(id));
                        return (
                          <button
                            type="button"
                            className={`cat-mini${isIn ? " is-in" : ""}`}
                            onClick={() => toggleAdmit(p.ids, !isIn)}
                            disabled={admitting === p.ids[0]}
                            title={isIn ? "Let into the Boudoir" : "Admit to the Boudoir"}
                          >
                            {admitting === p.ids[0] ? "…" : isIn ? "✓ In the Boudoir" : "Admit to Boudoir 🔒"}
                          </button>
                        );
                      })()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </details>

          <details className="vis-card vis-fold">
            <summary>
              <h3>✦ {only ? `Visits by ${who(people?.find((p) => p.visitor === only)?.name ?? null, only)}` : "Every visit"}</h3>
            </summary>
            {!only && (
              <p className="vis-person-actions">
                <button type="button" className="cat-mini" onClick={() => setHideMe((v) => !v)} aria-pressed={!hideMe}>
                  {hideMe ? "Show my own visits" : "Hide my own visits"}
                </button>
              </p>
            )}
            {logError && (
              <p className="cat-error" role="alert">
                {needsMigration(logError) ? "The visit log isn't set up yet: run supabase/migrations/20260915120000_palais_visitor_id.sql." : logError}
              </p>
            )}
            <ul className="vis-list vis-list--recent">
              {/* until the migration is run, the last 40 visits the old way */}
              {needsMigration(logError) &&
                stats.recent.map((v, i) => (
                  <li key={`old-${i}`}>
                    <span>
                      {flag(v.code)} {where(v)}
                      <small>
                        {v.visitor ? `visitor ${v.visitor} · ` : ""}
                        {deviceIcon(v.device)} {[deviceName(v.device), v.os, v.browser].filter(Boolean).join(" · ")}
                        {v.source ? ` · via ${v.source}` : ""}
                      </small>
                    </span>
                    <time dateTime={v.at}>{ago(v.at)}</time>
                  </li>
                ))}
              {feed.map((v) => (
                <li key={v.id}>
                  <span>
                    {flag(v.code)} {where(v)}
                    <small>
                      <b className={v.is_me ? "vis-name is-me" : "vis-name"}>{who(v.name, v.visitor, v.is_me)}</b>
                      {" · "}
                      {deviceIcon(v.device)} {machineOf(v) || deviceName(v.device)}
                      {v.source ? ` · via ${v.source}` : v.referrer ? ` · from ${v.referrer}` : ""}
                      {/* a link you tagged (?from=ig) vs. one that just opened inside the app:
                          both say "via Instagram", only the tagged one came through your link */}
                      {v.tag ? (
                        <>
                          {" · "}
                          <b className="vis-tag">🔗 your ?from={v.tag} link</b>
                        </>
                      ) : null}
                    </small>
                    {(v.network || v.vpn) && (
                      <small>
                        {v.vpn ? <b className="vis-vpn">🛡 {v.vpn}</b> : null}
                        {v.vpn && v.network ? " · " : ""}
                        {v.network}
                      </small>
                    )}
                    <small>
                      {[
                        v.seconds ? `stayed ${mins(v.seconds)}` : null,
                        v.pages && v.pages > 1 ? `${v.pages} pages` : null,
                        v.screen ? `screen ${v.screen}` : null,
                        v.viewport ? `window ${v.viewport}` : null,
                        v.dpr && v.dpr !== 1 ? `${v.dpr}×` : null,
                        v.language,
                        v.color_scheme === "dark" ? "dark mode" : null,
                        v.installed ? "home screen" : null,
                        v.connection,
                        v.cores ? `${v.cores} cores` : null,
                        v.memory ? `${v.memory} GB` : null,
                        v.timezone,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                    {v.landing && v.landing !== "/" && <small>🚪 came in at {placeName(v.landing.replace(/^#/, ""))}</small>}
                    {v.places.length > 0 && <small>🗺️ {v.places.map(stopName).join(" → ")}</small>}
                    {v.doings?.length > 0 && <small>✿ {doingTrail(v.doings)}</small>}
                  </span>
                  <time dateTime={v.at} title={new Date(v.at).toLocaleString()}>
                    {ago(v.at)}
                  </time>
                </li>
              ))}
              {!logDone && !logError && (
                <li ref={end} className="vis-more vis-more--load">
                  {loadingAll ? (
                    <span>Loading all visits…</span>
                  ) : (
                    <>
                      <span>More below as you scroll</span>
                      <button type="button" className="cat-mini" onClick={loadAll}>
                        Load all visits
                      </button>
                    </>
                  )}
                </li>
              )}
              {logDone && feed.length > 0 && (
                <li className="vis-more">
                  <span>{hideMe && !only ? "That's every visit but mine." : "That's every visit."}</span>
                </li>
              )}
              {logDone && !feed.length && !logError && (
                <li className="vis-more">
                  <span>No visits yet.</span>
                </li>
              )}
            </ul>
          </details>

          <section className="vis-card">
            <h3>🌍 Where in the world</h3>
            <VisitorMap cities={mapStats?.cities ?? stats.cities} total={mapStats?.cities_total ?? stats.cities_total} />
          </section>
          <p className="cat-signin-line">
            IP addresses aren't stored; each visitor is a scrambled code. Visits from localhost aren't counted.
          </p>
        </>
      )}
    </div>
  );
}
